import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";

import { MDSIconKind, MDSText, MDSTextColor, MDSTextVariant } from "@/mds";
import {
  AgentBreadcrumbs,
  type AgentBreadcrumbItem,
} from "@/pages/agent/components/agent-breadcrumbs";
import { AgentHorizontalCardCarousel } from "@/pages/agent/components/agent-horizontal-card-carousel";
import { AgentPageLayout } from "@/pages/agent/components/agent-page-layout";
import { AgentRelatedTasksCard } from "@/pages/agent/components/agent-related-tasks-card";
import { AgentRelationshipTile } from "@/pages/agent/components/agent-relationship-card";
import { AgentSection, AgentSectionEmptyState } from "@/pages/agent/components/agent-section";
import { AgentSurfaceNavigationActiveTab } from "@/pages/agent/components/agent-surface-navigation";
import { AgentGoalHeroCard } from "@/pages/agent/goals/agent-goal-hero-card";
import { useAgentIntentGraph } from "@/pages/agent/intent-graph/use-agent-intent-graph";
import { buildTaskRows, getTaskRowsForView } from "@/pages/agent/tasks/agent-tasks-page.utils";
import { TaskView } from "@/pages/agent/tasks/use-agent-tasks-search-params";
import { formatStatusLabel } from "@/pages/agent/utils/agent-status.utils";
import { useAppStore } from "@/store";
import {
  isClosedAssistantTaskStatus,
  normalizeAssistantTaskStatus,
} from "@/store/assistant/assistant-task-status";
import { AssistantTaskStatus } from "@/store/assistant/types";

export const AgentGoalPage = observer(({ goalId }: { goalId: string }) => {
  const { store } = useAppStore();
  const graph = useAgentIntentGraph();
  const goal = graph.data?.goals?.find(item => item.id === goalId);
  const parentGoal = goal?.parent_goal_id
    ? graph.data?.goals?.find(item => item.id === goal.parent_goal_id)
    : undefined;
  const childGoals = (goal?.child_goal_ids ?? [])
    .map(childId => graph.data?.goals?.find(item => item.id === childId))
    .filter(item => item !== undefined);
  const projectIds = new Set(goal?.project_ids ?? []);
  const projects = (goal?.project_ids ?? [])
    .map(projectId => store.assistantProjects.getCurrentAssistantModelById(projectId))
    .filter(project => project !== undefined);
  const tasks = store.assistantTasks.currentAssistantModels.filter(task =>
    task.projectId ? projectIds.has(task.projectId) : false
  );
  const taskRows = getTaskRowsForView({
    taskRows: buildTaskRows({
      tasks,
      projects: store.assistantProjects.currentAssistantModels,
      followUps: store.assistantFollowUps.currentAssistantModels,
      taskNoteReferences: store.assistantTaskNoteReferences.currentAssistantModels,
      getNoteById: store.notes.getNoteObservableById,
    }),
    view: TaskView.AllTasks,
  });
  const tasksLoaded =
    store.assistantTasks.currentAssistantModelsLoaded &&
    store.assistantProjects.currentAssistantModelsLoaded &&
    store.assistantFollowUps.currentAssistantModelsLoaded &&
    store.assistantTaskNoteReferences.currentAssistantModelsLoaded;
  const breadcrumbItems: AgentBreadcrumbItem[] = [
    { label: "Goals", onClick: () => store.agentOverlay.openGoals() },
  ];

  const toggleTaskComplete = async ({ taskId }: { taskId: string }) => {
    const task = store.assistantTasks.getCurrentAssistantModelById(taskId);
    if (!task) return;

    const currentStatus = normalizeAssistantTaskStatus({ status: task.status });
    const nextStatus = isClosedAssistantTaskStatus({ status: currentStatus })
      ? AssistantTaskStatus.Pending
      : AssistantTaskStatus.Completed;

    await task.updateStatus({ status: nextStatus });
  };

  if (parentGoal) {
    breadcrumbItems.push({
      label: parentGoal.title || "Untitled goal",
      onClick: () => store.agentOverlay.openGoal({ goalId: parentGoal.id }),
    });
  }

  breadcrumbItems.push({ label: goal?.title || "Goal" });

  return (
    <AgentPageLayout activeTab={AgentSurfaceNavigationActiveTab.Goals}>
      <AgentBreadcrumbs items={breadcrumbItems} />
      {graph.isLoading ? (
        <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.TextS}>
          Loading goal…
        </MDSText>
      ) : !goal ? (
        <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.TextS}>
          Goal not found.
        </MDSText>
      ) : (
        <StyledStack>
          <AgentGoalHeroCard goal={goal} />

          <AgentSection title="Related projects">
            {projects.length === 0 ? (
              <AgentSectionEmptyState>No related projects.</AgentSectionEmptyState>
            ) : (
              <AgentHorizontalCardCarousel ariaLabel="Related projects">
                {projects.map(project => (
                  <AgentRelationshipTile
                    key={project.id}
                    description={formatStatusLabel({ status: project.status })}
                    icon={MDSIconKind.Folder}
                    label="Project"
                    title={project.title || "Untitled project"}
                    onOpen={() => store.agentOverlay.openProject({ projectId: project.id })}
                  />
                ))}
              </AgentHorizontalCardCarousel>
            )}
          </AgentSection>

          <AgentRelatedTasksCard
            dataLoaded={tasksLoaded}
            emptyLabel="No related tasks."
            taskRows={taskRows}
            title="Related tasks"
            onOpenActivities={({ taskId }) =>
              store.agentOverlay.openActivities({ kind: "task", taskId })
            }
            onOpenNote={({ noteId }) => store.agentOverlay.openSourceNote({ noteId })}
            onOpenProject={({ projectId }) => store.agentOverlay.openProject({ projectId })}
            onOpenTask={({ taskId }) => store.agentOverlay.openTask({ taskId })}
            onToggleTaskComplete={toggleTaskComplete}
          />

          {parentGoal || childGoals.length > 0 ? (
            <AgentSection title="Related goals">
              <AgentHorizontalCardCarousel ariaLabel="Related goals">
                {parentGoal ? (
                  <AgentRelationshipTile
                    description={parentGoal.description}
                    icon={MDSIconKind.Compass}
                    label="Parent goal"
                    title={parentGoal.title || "Untitled goal"}
                    onOpen={() => store.agentOverlay.openGoal({ goalId: parentGoal.id })}
                  />
                ) : null}
                {childGoals.map(childGoal => (
                  <AgentRelationshipTile
                    key={childGoal.id}
                    description={childGoal.description}
                    icon={MDSIconKind.Compass}
                    label="Subgoal"
                    title={childGoal.title || "Untitled goal"}
                    onOpen={() => store.agentOverlay.openGoal({ goalId: childGoal.id })}
                  />
                ))}
              </AgentHorizontalCardCarousel>
            </AgentSection>
          ) : null}
        </StyledStack>
      )}
    </AgentPageLayout>
  );
});

const StyledStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[6]};
`;
